import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

// Utility para combinar classes CSS com Tailwind
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Formatação de moeda brasileira (R$)
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

// Formatação de número com separadores de milhares
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value)
}

// Formatação de datas
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions) {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  }).format(dateObj)
}

// Formatação de data e hora no formato brasileiro
export function formatDateTime(date: string | Date): string {
  if (!date) return '-'
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(dateObj)
}

// Formatação de data simples no formato dd/mm/yyyy
export function formatDateSimple(date: string | Date | null | undefined): string {
  if (!date) return ''
  
  // Se for string no formato ISO (YYYY-MM-DD), fazer parsing manual para evitar problemas de fuso horário
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-')
    return `${day}/${month}/${year}`
  }
  
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  // Verificar se a data é válida
  if (isNaN(dateObj.getTime())) return ''
  
  const day = dateObj.getDate().toString().padStart(2, '0')
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
  const year = dateObj.getFullYear()
  
  return `${day}/${month}/${year}`
}

// Formatação de CPF
export function formatarCPF(cpf: string): string {
  const numeros = cpf.replace(/[^\d]/g, '')
  if (numeros.length !== 11) return cpf
  return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

// Formatação de CNPJ
export function formatarCNPJ(cnpj: string): string {
  const numeros = cnpj.replace(/[^\d]/g, '')
  if (numeros.length !== 14) return cnpj
  return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

// Truncate string
export function truncate(str: string, length: number) {
  return str.length > length ? str.substring(0, length) + '...' : str
}

// Sleep function for async operations
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Formatação de HTML rico (limpa e sanitiza conteúdo Quill)
export function formatRichText(html: string): string {
  if (!html) return ''
  
  // Remove elementos vazios do Quill
  let cleanHtml = html.replace(/<p><br><\/p>/g, '')
  
  // Remove classes específicas do Quill que não são necessárias para visualização
  cleanHtml = cleanHtml.replace(/class="ql-ui"/g, '')
  cleanHtml = cleanHtml.replace(/contenteditable="false"/g, '')
  
  // Remove spans vazios do Quill UI
  cleanHtml = cleanHtml.replace(/<span class="ql-ui"[^>]*><\/span>/g, '')
  
  // Limpa atributos desnecessários para visualização
  cleanHtml = cleanHtml.replace(/data-list="[^"]*"/g, '')
  cleanHtml = cleanHtml.replace(/spellcheck="false"/g, '')
  
  // Remove divs de code-block do Quill e mantém apenas o conteúdo
  cleanHtml = cleanHtml.replace(/<div class="ql-code-block-container"[^>]*>/g, '<pre class="code-block">')
  cleanHtml = cleanHtml.replace(/<div class="ql-code-block">/g, '<code>')
  cleanHtml = cleanHtml.replace(/<\/div><\/div>/g, '</code></pre>')
  
  // Remove tags vazias
  cleanHtml = cleanHtml.replace(/<([^>]+)><\/\1>/g, '')
  
  return cleanHtml.trim()
}

// Sanitização básica de HTML (remove scripts e elementos perigosos)
export function sanitizeHtml(html: string): string {
  if (!html) return ''
  
  // Remove scripts, iframes e outros elementos potencialmente perigosos
  let sanitized = html.replace(/<script[^>]*>.*?<\/script>/gi, '')
  sanitized = sanitized.replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
  sanitized = sanitized.replace(/<object[^>]*>.*?<\/object>/gi, '')
  sanitized = sanitized.replace(/<embed[^>]*>/gi, '')
  sanitized = sanitized.replace(/<form[^>]*>.*?<\/form>/gi, '')
  
  // Remove eventos JavaScript
  sanitized = sanitized.replace(/on\w+="[^"]*"/gi, '')
  sanitized = sanitized.replace(/javascript:/gi, '')
  
  return sanitized
}

// Processa HTML do Quill para exibição segura e formatada
export function processRichTextForDisplay(html: string): string {
  if (!html) return ''

  let processed = html

  // Processar checkboxes markdown [ ] [x]
  processed = formatCheckboxMarkdown(processed)

  // Converter code blocks do Quill para <pre><code>
  processed = processed.replace(
    /<div[^>]*class="ql-code-block-container"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi,
    (_, inner) => {
      const code = inner
        .replace(/<div[^>]*class="ql-code-block"[^>]*>/gi, '')
        .replace(/<\/div>/gi, '\n')
        .trim()
      return `<pre><code>${code}</code></pre>`
    }
  )

  // Remover elementos de UI do Quill
  processed = processed.replace(/<span[^>]*class="ql-ui"[^>]*>[\s\S]*?<\/span>/gi, '')
  processed = processed.replace(/\s*contenteditable="false"/gi, '')
  processed = processed.replace(/\s*spellcheck="false"/gi, '')

  // Converter classes de tamanho do Quill para estilos inline
  processed = processed.replace(/class="ql-size-small"/gi, 'style="font-size: 0.75em"')
  processed = processed.replace(/class="ql-size-large"/gi, 'style="font-size: 1.5em"')
  processed = processed.replace(/class="ql-size-huge"/gi, 'style="font-size: 2.5em"')

  // Converter classes de alinhamento do Quill para estilos inline
  processed = processed.replace(/class="ql-align-center"/gi, 'style="text-align: center"')
  processed = processed.replace(/class="ql-align-right"/gi, 'style="text-align: right"')
  processed = processed.replace(/class="ql-align-justify"/gi, 'style="text-align: justify"')

  // Remover atributos Quill desnecessários para visualização
  processed = processed.replace(/\s*data-list="[^"]*"/gi, '')

  // Remover parágrafos vazios do Quill
  processed = processed.replace(/<p><br\s*\/?><\/p>/gi, '')

  // Envolver tabelas em container com scroll horizontal
  processed = processed.replace(/<table/gi, '<div class="table-wrapper"><table')
  processed = processed.replace(/<\/table>/gi, '</table></div>')

  // Sanitizar (remover scripts, iframes e eventos)
  processed = sanitizeHtml(processed)

  // Fixar protocolos de links e adicionar atributos de segurança
  if (typeof window !== 'undefined') {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(processed, 'text/html')

      doc.querySelectorAll('a').forEach(link => {
        const href = link.getAttribute('href') || ''
        // Adicionar https:// se não tiver protocolo
        if (href && !href.match(/^(https?:\/\/|mailto:|tel:|#|\/)/i)) {
          link.setAttribute('href', 'https://' + href)
        }
        // Abrir links externos em nova aba com segurança
        if (href && !href.startsWith('#') && !href.startsWith('/')) {
          link.setAttribute('target', '_blank')
          link.setAttribute('rel', 'noopener noreferrer')
        }
      })

      processed = doc.body.innerHTML
    } catch {
      // Fallback: regex para corrigir protocolos ausentes
      processed = processed.replace(
        /(<a[^>]*\shref=")(?!https?:\/\/|mailto:|tel:|#|\/)([^"]*")/gi,
        '$1https://$2'
      )
    }
  }

  return processed.trim()
}

// Extrai texto puro do HTML (para prévia/resumo)
export function extractPlainText(html: string): string {
  if (!html) return ''
  
  // Remove todas as tags HTML
  const plainText = html.replace(/<[^>]*>/g, ' ')
  
  // Remove múltiplos espaços e quebras de linha
  return plainText.replace(/\s+/g, ' ').trim()
}

// ============================================
// FORMATAÇÃO DE CHECKBOX E SELECT (Markdown-like)
// ============================================

/**
 * Processa sintaxe markdown de checkbox estilo GitHub para HTML
 * [ ] -> checkbox não marcado
 * [x] ou [X] -> checkbox marcado
 */
export function formatCheckboxMarkdown(html: string): string {
  if (!html) return ''
  
  // Padrão para encontrar [ ] ou [x] no texto
  const checkboxPattern = /\[([ xX])\]\s*/g
  let index = 0

  return html.replace(checkboxPattern, (_, checked) => {
    const isChecked = checked.toLowerCase() === 'x'
    const checkboxHtml = `<span class="inline-checkbox" data-index="${index}" data-checked="${isChecked}">
      <span class="checkbox-box ${isChecked ? 'checked' : ''}"></span>
    </span> `
    index++
    return checkboxHtml
  })
}

/**
 * Processa sintaxe customizada de select inline para HTML
 * Sintaxe: {select:Placeholder?Opção1|Opção2|Opção3}
 * Ou: {select:Opção1|Opção2|Opção3}
 * Com valor selecionado: {select:Placeholder?Opção1|*Opção2|Opção3}
 */
export function formatSelectMarkdown(html: string): string {
  if (!html) return ''
  
  const selectPattern = /\{select:([^}]+)\}/g

  return html.replace(selectPattern, (_, content: string) => {
    let placeholder = ''
    let optionsStr = content
    let selected = ''
    const selectId = `select-${Math.random().toString(36).substr(2, 9)}`

    // Verificar se tem placeholder (separado por ?)
    if (content.includes('?')) {
      const parts = content.split('?')
      placeholder = parts[0]
      optionsStr = parts.slice(1).join('?')
    }

    // Parse das opções (separadas por |)
    const options = optionsStr.split('|').map(opt => {
      const trimmed = opt.trim()
      // Verificar se está marcado como selecionado (*)
      if (trimmed.startsWith('*')) {
        selected = trimmed.substring(1)
        return trimmed.substring(1)
      }
      return trimmed
    }).filter(opt => opt.length > 0)

    // Gerar o HTML do select
    const optionsJson = JSON.stringify(options)
    let selectHtml = `<span class="ql-inline-select" data-options='${optionsJson}' data-selected="${selected}" data-placeholder="${placeholder}" data-select-id="${selectId}">`
    selectHtml += `<select class="ql-inline-select-dropdown">`

    if (placeholder) {
      selectHtml += `<option value="" disabled ${!selected ? 'selected' : ''}>${placeholder}</option>`
    }

    options.forEach(opt => {
      const isSelected = opt === selected
      selectHtml += `<option value="${opt}"${isSelected ? ' selected' : ''}>${opt}</option>`
    })

    selectHtml += `</select></span>`

    return selectHtml
  })
}

/**
 * Processa todo o conteúdo de texto rico, aplicando formatações de checkbox e select
 */
export function formatRichContent(html: string): string {
  if (!html) return ''
  
  let formatted = html
  
  // Aplicar formatação de checkbox
  formatted = formatCheckboxMarkdown(formatted)
  
  // Aplicar formatação de select
  formatted = formatSelectMarkdown(formatted)
  
  // Aplicar limpeza padrão de rich text
  formatted = formatRichText(formatted)
  
  return formatted
}

/**
 * Converte checkbox HTML de volta para sintaxe markdown
 */
export function checkboxToMarkdown(html: string): string {
  if (!html) return ''
  
  // Converter spans de checkbox para sintaxe markdown
  let result = html
  
  // Padrão para checkbox inline processado
  result = result.replace(
    /<span class="inline-checkbox"[^>]*data-checked="(true|false)"[^>]*>[\s\S]*?<\/span>\s*/g,
    (_, checked) => checked === 'true' ? '[x] ' : '[ ] '
  )
  
  // Padrão para checklist items do Quill
  result = result.replace(
    /<li class="ql-checklist-item[^"]*"[^>]*data-checked="(true|false)"[^>]*>([\s\S]*?)<\/li>/g,
    (_, checked, text) => checked === 'true' ? `[x] ${text}\n` : `[ ] ${text}\n`
  )
  
  return result
}

/**
 * Converte select HTML de volta para sintaxe markdown
 */
export function selectToMarkdown(html: string): string {
  if (!html) return ''
  
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  
  const selectElements = doc.querySelectorAll('.ql-inline-select')
  selectElements.forEach((element) => {
    const optionsStr = element.getAttribute('data-options') || '[]'
    const options = JSON.parse(optionsStr) as string[]
    const selected = element.getAttribute('data-selected') || ''
    const placeholder = element.getAttribute('data-placeholder') || ''
    
    // Marcar opção selecionada com *
    const optionsWithSelected = options.map(opt => 
      opt === selected ? `*${opt}` : opt
    )
    
    let markdown = '{select:'
    if (placeholder) {
      markdown += `${placeholder}?`
    }
    markdown += optionsWithSelected.join('|')
    markdown += '}'
    
    element.replaceWith(document.createTextNode(markdown))
  })
  
  return doc.body.innerHTML
}

/**
 * Converte todo o conteúdo de volta para sintaxe markdown
 */
export function richContentToMarkdown(html: string): string {
  if (!html) return ''
  
  let result = html
  
  // Converter checkboxes
  result = checkboxToMarkdown(result)
  
  // Converter selects
  result = selectToMarkdown(result)
  
  return result
}

/**
 * Extrai texto puro do HTML incluindo valores de checkbox e select
 * Útil para buscas e previews
 */
export function extractPlainTextWithInteractives(html: string): string {
  if (!html) return ''
  
  let text = html
  
  // Substituir checkboxes por indicadores de texto
  text = text.replace(/\[([ xX])\]\s*/g, (_, checked) => 
    checked.toLowerCase() === 'x' ? '☑ ' : '☐ '
  )
  text = text.replace(
    /<span class="inline-checkbox"[^>]*data-checked="(true|false)"[^>]*>[\s\S]*?<\/span>\s*/g,
    (_, checked) => checked === 'true' ? '☑ ' : '☐ '
  )
  text = text.replace(
    /<li class="ql-checklist-item[^"]*"[^>]*data-checked="(true|false)"[^>]*>([\s\S]*?)<\/li>/g,
    (_, checked, content) => (checked === 'true' ? '☑ ' : '☐ ') + content + '\n'
  )
  
  // Substituir selects pelo valor selecionado ou placeholder
  text = text.replace(/\{select:([^}]+)\}/g, (_, content: string) => {
    let optionsStr = content
    let selected = ''
    let placeholder = ''
    
    if (content.includes('?')) {
      const parts = content.split('?')
      placeholder = parts[0]
      optionsStr = parts.slice(1).join('?')
    }
    
    optionsStr.split('|').forEach(opt => {
      if (opt.trim().startsWith('*')) {
        selected = opt.trim().substring(1)
      }
    })
    
    return `[${selected || placeholder || 'Selecione'}]`
  })
  
  text = text.replace(
    /<span class="ql-inline-select"[^>]*data-selected="([^"]*)"[^>]*data-placeholder="([^"]*)"[^>]*>[\s\S]*?<\/span>/g,
    (_, selected, placeholder) => `[${selected || placeholder || 'Selecione'}]`
  )
  
  // Agora extrair texto puro
  return extractPlainText(text)
}